import { supabase } from '../supabase';
import { HELP_CATEGORIES, type HelpArticle, type HelpCategory, type HelpSource } from './types';
import type { Permission } from '../../context/RoleContext';

/**
 * Database-backed help articles (public.help_articles).
 *
 * Lets copy be corrected without a deploy — which matters because help text
 * goes stale against the product faster than anything else in the codebase.
 *
 * Never throws. A failed fetch resolves to the last good cached response, and
 * failing that to an empty list, because the composite source treats "no DB
 * articles" as "use the bundled ones" — a missing table or an offline phone
 * should degrade to shipped content, not to an error screen.
 */

const CACHE_KEY = 'fds_help_articles_v1';

interface HelpArticleRow {
    slug: string;
    title: string;
    category: string;
    summary: string | null;
    icon: string | null;
    sort_order: number | null;
    permission: string | null;
    visual: string | null;
    body: string | null;
}

function isCategory(value: string): value is HelpCategory {
    return (HELP_CATEGORIES as readonly string[]).includes(value);
}

function toArticle(row: HelpArticleRow): HelpArticle {
    return {
        slug: row.slug,
        title: row.title,
        // An unrecognised category shouldn't hide an article — park it in the
        // first section rather than dropping it on the floor.
        category: isCategory(row.category) ? row.category : 'Start here',
        summary: row.summary ?? '',
        icon: row.icon || 'FileText',
        order: row.sort_order ?? 99,
        permission: (row.permission as Permission) || undefined,
        visual: row.visual || undefined,
        body: (row.body ?? '').trim(),
        fromDb: true,
    };
}

function readCache(): HelpArticle[] | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        return raw ? (JSON.parse(raw) as HelpArticle[]) : null;
    } catch {
        return null;
    }
}

function writeCache(articles: HelpArticle[]) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(articles));
    } catch {
        // Quota or private mode — the cache is an optimisation, not a requirement.
    }
}

let inflight: Promise<HelpArticle[]> | null = null;

async function fetchArticles(): Promise<HelpArticle[]> {
    const { data, error } = await supabase
        .from('help_articles')
        .select('slug, title, category, summary, icon, sort_order, permission, visual, body')
        .eq('published', true);

    if (error) {
        // Includes 42P01 (table missing — migration not applied yet).
        return readCache() ?? [];
    }

    const articles = (data as HelpArticleRow[]).map(toArticle);
    writeCache(articles);
    return articles;
}

export const dbHelpSource: HelpSource = {
    async listArticles() {
        // De-duplicate concurrent calls — the index and an article view can
        // mount together, and this is one request either way.
        if (!inflight) {
            inflight = fetchArticles().finally(() => { inflight = null; });
        }
        return inflight;
    },
    async getArticle(slug: string) {
        const articles = await this.listArticles();
        return articles.find((a) => a.slug === slug) ?? null;
    },
};
